import type {GeoObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {RootManagerNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import type {ReturnedNodeDataDict} from '../../ExampleUtils';
import type {ActorSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Actor';
import type {CameraControlsSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/CameraControls';
import type {MaterialsNetworkSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import {ParamType} from '../../../../../@polygonjs/polygonjs/src/engine/poly/ParamType';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		const lights_nodes: ReturnedNodeDataDict = {};
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
			hemisphereLight1.uiData.setPosition(50, -50);
			hemisphereLight1.p.intensity.set(0.52);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return {node: hemisphereLight1, children: hemisphereLight1_nodes};
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			const spotLight1_nodes: ReturnedNodeDataDict = {};
			spotLight1.uiData.setPosition(300, -50);
			spotLight1.p.decay.set(0.1);
			spotLight1.p.distance.set(10);
			spotLight1.p.castShadow.set(true);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return {node: spotLight1, children: spotLight1_nodes};
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(300, 150);
			polarTransform1.p.center.set([0, 0.7, 0]);
			polarTransform1.p.latitude.set(25.2);
			polarTransform1.p.depth.set(3);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(100, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		lights_nodes['hemisphereLight1'] = create_hemisphereLight1(lights);
		lights_nodes['spotLight1'] = create_spotLight1(lights);
		lights_nodes['polarTransform1'] = create_polarTransform1(lights);
		lights_nodes['merge1'] = create_merge1(lights);
		lights_nodes['polarTransform1'].node.setInput(0, lights_nodes['spotLight1'].node);
		lights_nodes['merge1'].node.setInput(0, lights_nodes['hemisphereLight1'].node);
		lights_nodes['merge1'].node.setInput(1, lights_nodes['polarTransform1'].node);
		if (lights.childrenController) {
			lights.childrenController.selection.set([lights_nodes['hemisphereLight1'].node]);
		}
		lights.uiData.setPosition(-150, -250);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return {node: lights, children: lights_nodes};
	}
	function create_cameras(parentNode: RootManagerNode) {
		var cameras = parentNode.createNode('geo');
		cameras.setName('cameras');
		const cameras_nodes: ReturnedNodeDataDict = {};
		function create_cameraControls1(cameras: GeoObjNode) {
			var cameraControls1 = cameras.createNode('cameraControls');
			cameraControls1.setName('cameraControls1');
			const cameraControls1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(cameraControls1: CameraControlsSopNode) {
				var cameraOrbitControls1 = cameraControls1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(0, 0);
				cameraOrbitControls1.p.target.set([0, 0.75, 0]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			cameraControls1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(cameraControls1);
			if (cameraControls1.childrenController) {
				cameraControls1.childrenController.selection.set([]);
			}
			cameraControls1.uiData.setPosition(0, 150);
			cameraControls1.p.node.set('cameraOrbitControls1');
			cameraControls1.params.postCreateSpareParams();
			cameraControls1.params.runOnSceneLoadHooks();
			return {node: cameraControls1, children: cameraControls1_nodes};
		}
		function create_perspectiveCamera_DEBUG(cameras: GeoObjNode) {
			var perspectiveCamera_DEBUG = cameras.createNode('perspectiveCamera');
			perspectiveCamera_DEBUG.setName('perspectiveCamera_DEBUG');
			const perspectiveCamera_DEBUG_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera_DEBUG.uiData.setPosition(0, -50);
			perspectiveCamera_DEBUG.p.position.set([3.3, 3.3, 3.3]);
			perspectiveCamera_DEBUG.params.postCreateSpareParams();
			perspectiveCamera_DEBUG.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera_DEBUG, children: perspectiveCamera_DEBUG_nodes};
		}
		function create_mapboxCamera1(cameras: GeoObjNode) {
			var mapboxCamera1 = cameras.createNode('mapboxCamera');
			mapboxCamera1.setName('mapboxCamera1');
			const mapboxCamera1_nodes: ReturnedNodeDataDict = {};
			mapboxCamera1.uiData.setPosition(200, 50);
			mapboxCamera1.params.postCreateSpareParams();
			mapboxCamera1.params.runOnSceneLoadHooks();
			return {node: mapboxCamera1, children: mapboxCamera1_nodes};
		}
		function create_merge1(cameras: GeoObjNode) {
			var merge1 = cameras.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(50, 400);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		cameras_nodes['cameraControls1'] = create_cameraControls1(cameras);
		cameras_nodes['perspectiveCamera_DEBUG'] = create_perspectiveCamera_DEBUG(cameras);
		cameras_nodes['mapboxCamera1'] = create_mapboxCamera1(cameras);
		cameras_nodes['merge1'] = create_merge1(cameras);
		cameras_nodes['cameraControls1'].node.setInput(0, cameras_nodes['perspectiveCamera_DEBUG'].node);
		cameras_nodes['merge1'].node.setInput(0, cameras_nodes['cameraControls1'].node);
		cameras_nodes['merge1'].node.setInput(1, cameras_nodes['mapboxCamera1'].node);
		if (cameras.childrenController) {
			cameras.childrenController.selection.set([cameras_nodes['mapboxCamera1'].node]);
		}
		cameras.uiData.setPosition(-150, -150);
		cameras.flags.display.set(true);
		cameras.params.postCreateSpareParams();
		cameras.params.runOnSceneLoadHooks();
		return {node: cameras, children: cameras_nodes};
	}
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		const geo1_nodes: ReturnedNodeDataDict = {};
		function create_emptyObject1(geo1: GeoObjNode) {
			var emptyObject1 = geo1.createNode('emptyObject');
			emptyObject1.setName('emptyObject1');
			const emptyObject1_nodes: ReturnedNodeDataDict = {};
			emptyObject1.uiData.setPosition(-350, 850);
			emptyObject1.params.postCreateSpareParams();
			emptyObject1.params.runOnSceneLoadHooks();
			return {node: emptyObject1, children: emptyObject1_nodes};
		}
		function create_actor1(geo1: GeoObjNode) {
			var actor1 = geo1.createNode('actor');
			actor1.setName('actor1');
			const actor1_nodes: ReturnedNodeDataDict = {};
			function create_rayFromCursor1(actor1: ActorSopNode) {
				var rayFromCursor1 = actor1.createNode('rayFromCursor');
				rayFromCursor1.setName('rayFromCursor1');
				const rayFromCursor1_nodes: ReturnedNodeDataDict = {};
				rayFromCursor1.uiData.setPosition(200, 200);
				rayFromCursor1.params.postCreateSpareParams();
				rayFromCursor1.params.runOnSceneLoadHooks();
				return {node: rayFromCursor1, children: rayFromCursor1_nodes};
			}
			function create_rayIntersectPlane1(actor1: ActorSopNode) {
				var rayIntersectPlane1 = actor1.createNode('rayIntersectPlane');
				rayIntersectPlane1.setName('rayIntersectPlane1');
				const rayIntersectPlane1_nodes: ReturnedNodeDataDict = {};
				rayIntersectPlane1.uiData.setPosition(400, 350);
				rayIntersectPlane1.params.postCreateSpareParams();
				rayIntersectPlane1.params.runOnSceneLoadHooks();
				return {node: rayIntersectPlane1, children: rayIntersectPlane1_nodes};
			}
			function create_plane1(actor1: ActorSopNode) {
				var plane1 = actor1.createNode('plane');
				plane1.setName('plane1');
				const plane1_nodes: ReturnedNodeDataDict = {};
				plane1.uiData.setPosition(200, 500);
				plane1.params.postCreateSpareParams();
				plane1.params.runOnSceneLoadHooks();
				return {node: plane1, children: plane1_nodes};
			}
			function create_onPointerdown1(actor1: ActorSopNode) {
				var onPointerdown1 = actor1.createNode('onPointerdown');
				onPointerdown1.setName('onPointerdown1');
				const onPointerdown1_nodes: ReturnedNodeDataDict = {};
				onPointerdown1.uiData.setPosition(300, -250);
				onPointerdown1.params.postCreateSpareParams();
				onPointerdown1.params.runOnSceneLoadHooks();
				return {node: onPointerdown1, children: onPointerdown1_nodes};
			}
			function create_setObjectPosition1(actor1: ActorSopNode) {
				var setObjectPosition1 = actor1.createNode('setObjectPosition');
				setObjectPosition1.setName('setObjectPosition1');
				const setObjectPosition1_nodes: ReturnedNodeDataDict = {};
				setObjectPosition1.uiData.setPosition(800, -50);
				setObjectPosition1.params.postCreateSpareParams();
				setObjectPosition1.params.runOnSceneLoadHooks();
				return {node: setObjectPosition1, children: setObjectPosition1_nodes};
			}
			function create_createObjects1(actor1: ActorSopNode) {
				var createObjects1 = actor1.createNode('createObjects');
				createObjects1.setName('createObjects1');
				const createObjects1_nodes: ReturnedNodeDataDict = {};
				createObjects1.uiData.setPosition(1150, -50);
				createObjects1.params.postCreateSpareParams();
				createObjects1.params.runOnSceneLoadHooks();
				return {node: createObjects1, children: createObjects1_nodes};
			}
			function create_getObjectChild1(actor1: ActorSopNode) {
				var getObjectChild1 = actor1.createNode('getObjectChild');
				getObjectChild1.setName('getObjectChild1');
				const getObjectChild1_nodes: ReturnedNodeDataDict = {};
				getObjectChild1.uiData.setPosition(300, -100);
				getObjectChild1.params.postCreateSpareParams();
				getObjectChild1.params.runOnSceneLoadHooks();
				return {node: getObjectChild1, children: getObjectChild1_nodes};
			}
			function create_getObject1(actor1: ActorSopNode) {
				var getObject1 = actor1.createNode('getObject');
				getObject1.setName('getObject1');
				const getObject1_nodes: ReturnedNodeDataDict = {};
				getObject1.uiData.setPosition(-250, -200);
				getObject1.p.getCurrentObject.set(false);
				getObject1.p.mask.set('*/objectsPool');
				getObject1.params.postCreateSpareParams();
				getObject1.params.runOnSceneLoadHooks();
				return {node: getObject1, children: getObject1_nodes};
			}
			function create_onTick1(actor1: ActorSopNode) {
				var onTick1 = actor1.createNode('onTick');
				onTick1.setName('onTick1');
				const onTick1_nodes: ReturnedNodeDataDict = {};
				onTick1.uiData.setPosition(-350, 0);
				onTick1.params.postCreateSpareParams();
				onTick1.params.runOnSceneLoadHooks();
				return {node: onTick1, children: onTick1_nodes};
			}
			function create_multAdd1(actor1: ActorSopNode) {
				var multAdd1 = actor1.createNode('multAdd');
				multAdd1.setName('multAdd1');
				const multAdd1_nodes: ReturnedNodeDataDict = {};
				multAdd1.uiData.setPosition(-250, 0);
				multAdd1.addParam(ParamType.FLOAT, 'value', 0, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				multAdd1.addParam(ParamType.FLOAT, 'preAdd', 0, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				multAdd1.addParam(ParamType.FLOAT, 'mult', 1, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				multAdd1.params.get('mult')!.set(0);
				multAdd1.addParam(ParamType.FLOAT, 'postAdd', 0, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				multAdd1.params.postCreateSpareParams();
				multAdd1.params.runOnSceneLoadHooks();
				return {node: multAdd1, children: multAdd1_nodes};
			}
			function create_floatToInt1(actor1: ActorSopNode) {
				var floatToInt1 = actor1.createNode('floatToInt');
				floatToInt1.setName('floatToInt1');
				const floatToInt1_nodes: ReturnedNodeDataDict = {};
				floatToInt1.uiData.setComment(
					'even though we set the `index` of the `getObjectChild` to 0, we need to link to to time (multiplied by zero), so that the output of getObjectChild is not cached.'
				);
				floatToInt1.uiData.setPosition(-150, 0);
				floatToInt1.params.postCreateSpareParams();
				floatToInt1.params.runOnSceneLoadHooks();
				return {node: floatToInt1, children: floatToInt1_nodes};
			}
			actor1_nodes['rayFromCursor1'] = create_rayFromCursor1(actor1);
			actor1_nodes['rayIntersectPlane1'] = create_rayIntersectPlane1(actor1);
			actor1_nodes['plane1'] = create_plane1(actor1);
			actor1_nodes['onPointerdown1'] = create_onPointerdown1(actor1);
			actor1_nodes['setObjectPosition1'] = create_setObjectPosition1(actor1);
			actor1_nodes['createObjects1'] = create_createObjects1(actor1);
			actor1_nodes['getObjectChild1'] = create_getObjectChild1(actor1);
			actor1_nodes['getObject1'] = create_getObject1(actor1);
			actor1_nodes['onTick1'] = create_onTick1(actor1);
			actor1_nodes['multAdd1'] = create_multAdd1(actor1);
			actor1_nodes['floatToInt1'] = create_floatToInt1(actor1);
			actor1_nodes['rayIntersectPlane1'].node.setInput('Ray', actor1_nodes['rayFromCursor1'].node, 'Ray');
			actor1_nodes['rayIntersectPlane1'].node.setInput('Plane', actor1_nodes['plane1'].node, 'Plane');
			actor1_nodes['setObjectPosition1'].node.setInput('trigger', actor1_nodes['onPointerdown1'].node, 'trigger');
			actor1_nodes['setObjectPosition1'].node.setInput(
				'Object3D',
				actor1_nodes['getObjectChild1'].node,
				'Object3D'
			);
			actor1_nodes['setObjectPosition1'].node.setInput(
				'position',
				actor1_nodes['rayIntersectPlane1'].node,
				'position'
			);
			actor1_nodes['createObjects1'].node.setInput('trigger', actor1_nodes['setObjectPosition1'].node, 'trigger');
			actor1_nodes['createObjects1'].node.setInput('child', actor1_nodes['setObjectPosition1'].node, 'Object3D');
			actor1_nodes['getObjectChild1'].node.setInput('Object3D', actor1_nodes['getObject1'].node, 'Object3D');
			actor1_nodes['getObjectChild1'].node.setInput('index', actor1_nodes['floatToInt1'].node, 'int');
			actor1_nodes['multAdd1'].node.setInput('value', actor1_nodes['onTick1'].node, 'time');
			actor1_nodes['floatToInt1'].node.setInput('float', actor1_nodes['multAdd1'].node, 'val');
			if (actor1.childrenController) {
				actor1.childrenController.selection.set([]);
			}
			actor1.uiData.setPosition(-350, 1050);
			actor1.params.postCreateSpareParams();
			actor1.params.runOnSceneLoadHooks();
			return {node: actor1, children: actor1_nodes};
		}
		function create_transform4(geo1: GeoObjNode) {
			var transform4 = geo1.createNode('transform');
			transform4.setName('transform4');
			const transform4_nodes: ReturnedNodeDataDict = {};
			transform4.uiData.setPosition(50, 800);
			transform4.p.scale.set(16);
			transform4.params.postCreateSpareParams();
			transform4.params.runOnSceneLoadHooks();
			return {node: transform4, children: transform4_nodes};
		}
		function create_text1(geo1: GeoObjNode) {
			var text1 = geo1.createNode('text');
			text1.setName('text1');
			const text1_nodes: ReturnedNodeDataDict = {};
			text1.uiData.setPosition(50, 500);
			text1.p.text.set("`copy('../copy1')`");
			text1.params.postCreateSpareParams();
			text1.params.runOnSceneLoadHooks();
			return {node: text1, children: text1_nodes};
		}
		function create_copy1(geo1: GeoObjNode) {
			var copy1 = geo1.createNode('copy');
			copy1.setName('copy1');
			const copy1_nodes: ReturnedNodeDataDict = {};
			copy1.uiData.setPosition(50, 600);
			copy1.p.count.set(10);
			copy1.params.postCreateSpareParams();
			copy1.params.runOnSceneLoadHooks();
			return {node: copy1, children: copy1_nodes};
		}
		function create_hierarchy1(geo1: GeoObjNode) {
			var hierarchy1 = geo1.createNode('hierarchy');
			hierarchy1.setName('hierarchy1');
			const hierarchy1_nodes: ReturnedNodeDataDict = {};
			hierarchy1.uiData.setPosition(50, 1050);
			hierarchy1.params.postCreateSpareParams();
			hierarchy1.params.runOnSceneLoadHooks();
			return {node: hierarchy1, children: hierarchy1_nodes};
		}
		function create_objectProperties1(geo1: GeoObjNode) {
			var objectProperties1 = geo1.createNode('objectProperties');
			objectProperties1.setName('objectProperties1');
			const objectProperties1_nodes: ReturnedNodeDataDict = {};
			objectProperties1.uiData.setPosition(50, 1150);
			objectProperties1.p.tname.set(true);
			objectProperties1.p.name.set('objectsPool');
			objectProperties1.p.tvisible.set(true);
			objectProperties1.p.visible.set(false);
			objectProperties1.params.postCreateSpareParams();
			objectProperties1.params.runOnSceneLoadHooks();
			return {node: objectProperties1, children: objectProperties1_nodes};
		}
		function create_merge1(geo1: GeoObjNode) {
			var merge1 = geo1.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(-150, 1350);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		function create_objectProperties2(geo1: GeoObjNode) {
			var objectProperties2 = geo1.createNode('objectProperties');
			objectProperties2.setName('objectProperties2');
			const objectProperties2_nodes: ReturnedNodeDataDict = {};
			objectProperties2.uiData.setPosition(50, 900);
			objectProperties2.p.tname.set(true);
			objectProperties2.p.name.set('obj`@objnum`');
			objectProperties2.params.postCreateSpareParams();
			objectProperties2.params.runOnSceneLoadHooks();
			return {node: objectProperties2, children: objectProperties2_nodes};
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(50, 700);
			material1.p.material.set('../MAT/meshStandard1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandard1(MAT: MaterialsNetworkSopNode) {
				var meshStandard1 = MAT.createNode('meshStandard');
				meshStandard1.setName('meshStandard1');
				const meshStandard1_nodes: ReturnedNodeDataDict = {};
				meshStandard1.uiData.setPosition(0, 0);
				meshStandard1.p.color.set([0, 0.002428215868235294, 1]);
				meshStandard1.params.postCreateSpareParams();
				meshStandard1.params.runOnSceneLoadHooks();
				return {node: meshStandard1, children: meshStandard1_nodes};
			}
			MAT_nodes['meshStandard1'] = create_meshStandard1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([MAT_nodes['meshStandard1'].node]);
			}
			MAT.uiData.setPosition(-350, 650);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		geo1_nodes['emptyObject1'] = create_emptyObject1(geo1);
		geo1_nodes['actor1'] = create_actor1(geo1);
		geo1_nodes['transform4'] = create_transform4(geo1);
		geo1_nodes['text1'] = create_text1(geo1);
		geo1_nodes['copy1'] = create_copy1(geo1);
		geo1_nodes['hierarchy1'] = create_hierarchy1(geo1);
		geo1_nodes['objectProperties1'] = create_objectProperties1(geo1);
		geo1_nodes['merge1'] = create_merge1(geo1);
		geo1_nodes['objectProperties2'] = create_objectProperties2(geo1);
		geo1_nodes['material1'] = create_material1(geo1);
		geo1_nodes['MAT'] = create_MAT(geo1);
		geo1_nodes['actor1'].node.setInput(0, geo1_nodes['emptyObject1'].node);
		geo1_nodes['transform4'].node.setInput(0, geo1_nodes['material1'].node);
		geo1_nodes['copy1'].node.setInput(0, geo1_nodes['text1'].node);
		geo1_nodes['hierarchy1'].node.setInput(0, geo1_nodes['objectProperties2'].node);
		geo1_nodes['objectProperties1'].node.setInput(0, geo1_nodes['hierarchy1'].node);
		geo1_nodes['merge1'].node.setInput(0, geo1_nodes['actor1'].node);
		geo1_nodes['merge1'].node.setInput(1, geo1_nodes['objectProperties1'].node);
		geo1_nodes['objectProperties2'].node.setInput(0, geo1_nodes['transform4'].node);
		geo1_nodes['material1'].node.setInput(0, geo1_nodes['copy1'].node);
		if (geo1.childrenController) {
			geo1.childrenController.selection.set([]);
		}
		geo1.uiData.setPosition(-150, -350);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return {node: geo1, children: geo1_nodes};
	}
	scene_root_nodes['lights'] = create_lights(root);
	scene_root_nodes['cameras'] = create_cameras(root);
	scene_root_nodes['geo1'] = create_geo1(root);
	scene.play();
	return {
		scene,
		cameraPath: '/cameras/cameras:sopGroup/mapboxCamera1',
		focusedNodes: Object.values(scene_root_nodes['geo1'].children['actor1'].children).map((n) => n.node),
		autoLayout: false,
	};
}
// export const sopMapboxCamera_basic = [
